import { motion, useReducedMotion } from 'motion/react'
import { useShopI18n } from '../../i18n'
import { buildFieldUiHelpers } from './customer-auth/CustomerAuthForm.helpers'
import {
  ActivationCardShell,
  ActivationLoadingView,
  ActivationRecoveryIntro,
  ActivationRecoveryView,
  ActivationSetupIntro,
  ActivationSetupView,
  ActivationSuccessView,
  useActivationFlowEffects,
  useActivationFlowReducer,
  useActivationSubmitHandlers,
  useActivationViewModel,
} from './customer-auth/activation'

export const CustomerActivateAccountPage = () => {
  const { t, toLocalizedPath } = useShopI18n()
  const prefersReducedMotion = Boolean(useReducedMotion())
  const [state, dispatch] = useActivationFlowReducer()
  useActivationFlowEffects({ dispatch })
  const viewModel = useActivationViewModel({
    state,
    t,
    prefersReducedMotion,
  })

  const fieldUi = buildFieldUiHelpers({
    fieldErrors: viewModel.fieldErrors,
    onFieldBlur: (field) => {
      if (field === 'password') {
        dispatch({ type: 'SETUP_PASSWORD_TOUCHED' })
      }
      if (field === 'confirmPassword') {
        dispatch({ type: 'SETUP_CONFIRM_PASSWORD_TOUCHED' })
      }
    },
  })

  const loginHref = toLocalizedPath('/dashboard/login')
  const homeHref = toLocalizedPath('/')

  const { handleResendActivation, handleSetupSubmit } = useActivationSubmitHandlers({
    dispatch,
    token: state.token,
    password: state.setup.password,
    activationFieldsValid: viewModel.activationFieldsValid,
    normalizedRecoveryEmail: viewModel.normalizedRecoveryEmail,
    recoveryEmailValid: viewModel.recoveryEmailValid,
    messages: viewModel.copy.submitMessages,
  })

  return (
    <div className="ca-page ca-theme-transition py-6 sm:py-10 lg:py-14">
      <div className="shop-layout">
        <section className="relative flex min-h-[62vh] items-start lg:min-h-[68vh]">
          <div className="mx-auto w-full max-w-[71rem]">
            <div className="relative grid items-start gap-y-6 gap-x-6 md:gap-x-8 lg:grid-cols-12 lg:gap-x-10">
              <motion.div
                initial={viewModel.motion.pageInitialMotion}
                animate={viewModel.motion.pageMotion}
                transition={viewModel.motion.stateTransition}
                className="flex h-full flex-col gap-5 lg:col-span-7 lg:gap-6"
              >
                {viewModel.isRecoveryState ? (
                  <ActivationRecoveryIntro
                    leftEyebrow={viewModel.copy.recoveryIntro.leftEyebrow}
                    leftTitle={viewModel.copy.recoveryIntro.leftTitle}
                    leftSubtitle={viewModel.copy.recoveryIntro.leftSubtitle}
                    nextStepsTitle={viewModel.copy.recoveryIntro.nextStepsTitle}
                    nextSteps={viewModel.copy.recoveryIntro.nextSteps}
                    trustNote={viewModel.copy.recoveryIntro.trustNote}
                    securityPanelTitle={viewModel.copy.recoveryIntro.securityPanelTitle}
                    securityPanelItems={viewModel.copy.recoveryIntro.securityPanelItems}
                    prefersReducedMotion={prefersReducedMotion}
                  />
                ) : (
                  <ActivationSetupIntro
                    customerAreaLabel={viewModel.copy.setupIntro.customerAreaLabel}
                    title={viewModel.copy.setupIntro.title}
                    subtitle={viewModel.copy.setupIntro.subtitle}
                    checklistItems={viewModel.copy.setupIntro.checklistItems}
                    contextNote={viewModel.copy.setupIntro.contextNote}
                    showChecklist={viewModel.setupIntroChecklistVisible}
                  />
                )}
              </motion.div>

              <motion.div
                initial={viewModel.motion.cardInitialMotion}
                animate={viewModel.motion.pageMotion}
                transition={{ ...viewModel.motion.stateTransition, delay: prefersReducedMotion ? 0 : 0.05 }}
                className="w-full lg:col-span-5 lg:justify-self-end lg:max-w-[29rem]"
              >
                <ActivationCardShell
                  cardStateKey={viewModel.cardStateKey}
                  prefersReducedMotion={prefersReducedMotion}
                  stateTransition={viewModel.motion.stateTransition}
                >
                  {state.status === 'success' ? (
                    <ActivationSuccessView
                      title={viewModel.copy.success.title}
                      body={viewModel.copy.success.body}
                      goToLoginLabel={viewModel.copy.success.goToLoginLabel}
                      goHomeLabel={viewModel.copy.success.goHomeLabel}
                      loginHref={loginHref}
                      homeHref={homeHref}
                    />
                  ) : state.status === 'loading' ? (
                    <ActivationLoadingView
                      title={viewModel.copy.loading.title}
                      body={viewModel.copy.loading.body}
                    />
                  ) : viewModel.isRecoveryState ? (
                    <ActivationRecoveryView
                      success={state.recovery.success}
                      email={state.recovery.email}
                      emailError={viewModel.recoveryEmailError}
                      submitting={state.recovery.submitState === 'pending'}
                      feedback={state.recovery.feedback}
                      feedbackTone={state.recovery.feedbackTone}
                      loginHref={loginHref}
                      cardEyebrow={viewModel.copy.recoveryView.cardEyebrow}
                      cardTitle={viewModel.copy.recoveryView.cardTitle}
                      cardSubtitle={viewModel.copy.recoveryView.cardSubtitle}
                      emailLabel={viewModel.copy.recoveryView.emailLabel}
                      emailPlaceholder={viewModel.copy.recoveryView.emailPlaceholder}
                      submitCta={viewModel.copy.recoveryView.submitCta}
                      submittingCta={viewModel.copy.recoveryView.submittingCta}
                      submitHint={viewModel.copy.recoveryView.submitHint}
                      securityNote={viewModel.copy.recoveryView.securityNote}
                      helper={viewModel.copy.recoveryView.helper}
                      successTitle={viewModel.copy.recoveryView.successTitle}
                      successBody={viewModel.copy.recoveryView.successBody}
                      successHint={viewModel.copy.recoveryView.successHint}
                      successSpamHint={viewModel.copy.recoveryView.successSpamHint}
                      backToLoginLabel={viewModel.copy.recoveryView.backToLoginLabel}
                      prefersReducedMotion={prefersReducedMotion}
                      onEmailChange={(value) => {
                        dispatch({ type: 'RECOVERY_EMAIL_CHANGED', value })
                      }}
                      onEmailBlur={() => {
                        dispatch({ type: 'RECOVERY_EMAIL_TOUCHED' })
                      }}
                      onSubmit={handleResendActivation}
                    />
                  ) : (
                    <ActivationSetupView
                      customerAreaLabel={viewModel.copy.setupView.customerAreaLabel}
                      cardTitle={viewModel.copy.setupView.cardTitle}
                      cardSubtitle={viewModel.copy.setupView.cardSubtitle}
                      sectionPasswordTitle={viewModel.copy.setupView.sectionPasswordTitle}
                      passwordLabel={viewModel.copy.setupView.passwordLabel}
                      passwordPlaceholder={viewModel.copy.setupView.passwordPlaceholder}
                      passwordShowLabel={viewModel.copy.setupView.passwordShowLabel}
                      passwordHideLabel={viewModel.copy.setupView.passwordHideLabel}
                      confirmPasswordLabel={viewModel.copy.setupView.confirmPasswordLabel}
                      confirmPasswordPlaceholder={viewModel.copy.setupView.confirmPasswordPlaceholder}
                      requirementsTitle={viewModel.copy.setupView.requirementsTitle}
                      requirements={viewModel.passwordRequirements}
                      passwordValidLabel={viewModel.copy.setupView.passwordValidLabel}
                      showPasswordValid={viewModel.passwordReady}
                      sectionSecurityTitle={viewModel.copy.setupView.sectionSecurityTitle}
                      sectionSecuritySubtitle={viewModel.copy.setupView.sectionSecuritySubtitle}
                      sectionTwoFactorTitle={viewModel.copy.setupView.sectionTwoFactorTitle}
                      sectionTwoFactorSubtitle={viewModel.copy.setupView.sectionTwoFactorSubtitle}
                      optionalBadge={viewModel.copy.setupView.optionalBadge}
                      twoFactorFoundationNote={viewModel.copy.setupView.twoFactorFoundationNote}
                      submitCta={viewModel.copy.setupView.submitCta}
                      submittingCta={viewModel.copy.setupView.submittingCta}
                      profileSettingsNote={viewModel.copy.setupView.profileSettingsNote}
                      trustFooter={viewModel.copy.setupView.trustFooter}
                      backToLoginLabel={viewModel.copy.setupView.backToLoginLabel}
                      loginHref={loginHref}
                      password={state.setup.password}
                      confirmPassword={state.setup.confirmPassword}
                      submitting={state.setup.submitState === 'pending'}
                      activationFieldsValid={viewModel.activationFieldsValid}
                      formError={state.setup.error}
                      fieldUi={fieldUi}
                      onPasswordChange={(value) => {
                        dispatch({ type: 'SETUP_PASSWORD_CHANGED', value })
                      }}
                      onConfirmPasswordChange={(value) => {
                        dispatch({ type: 'SETUP_CONFIRM_PASSWORD_CHANGED', value })
                      }}
                      onSubmit={handleSetupSubmit}
                    />
                  )}
                </ActivationCardShell>
              </motion.div>
            </div>
          </div>
        </section>
      </div>
    </div>
  )
}
